
import React, { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getProximityBgColor } from "@/types/game";

interface RoundLabelProps {
  roundNumber: number;
  proximityScore?: number;
  isCurrentRound?: boolean;
}

export const RoundLabel: React.FC<RoundLabelProps> = ({ 
  roundNumber, 
  proximityScore,
  isCurrentRound = false
}) => {
  const [leftLineWidth, setLeftLineWidth] = useState("0%");
  const [rightLineWidth, setRightLineWidth] = useState("0%");
  const [cachedProximityScore, setCachedProximityScore] = useState<number | undefined>(proximityScore);

  useEffect(() => {
    if (proximityScore !== undefined) {
      setCachedProximityScore(proximityScore);
    }
  }, [proximityScore]);

  useEffect(() => {
    if (proximityScore !== undefined) {
      // Reset the lines to 0 width
      setLeftLineWidth("0%");
      setRightLineWidth("0%");
      
      // Start animation after a short delay
      const timer = setTimeout(() => {
        const widthPercentage = `${100 - proximityScore}%`;
        setLeftLineWidth(widthPercentage);
        setRightLineWidth(widthPercentage);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [proximityScore]);

  const scoreToUse = proximityScore !== undefined ? proximityScore : cachedProximityScore;

  const lineColorClass = scoreToUse !== undefined 
    ? getProximityBgColor(scoreToUse) 
    : "bg-border";

  console.log(`Round ${roundNumber}: proximityScore=${proximityScore}, cachedScore=${cachedProximityScore}, lineColorClass=${lineColorClass}`);

  let borderColorClass = "border-border";
  if (scoreToUse !== undefined) {
    if (scoreToUse === 100) borderColorClass = "border-blue-500";
    else if (scoreToUse >= 75) borderColorClass = "border-green-500";
    else if (scoreToUse >= 50) borderColorClass = "border-amber-500";
    else if (scoreToUse >= 25) borderColorClass = "border-orange-500";
    else borderColorClass = "border-red-500";
  }

  const getProximityDescription = () => {
    if (scoreToUse === undefined) return null;
    if (scoreToUse === 100) return "Perfect match!";
    if (scoreToUse >= 75) return "Very close!";
    if (scoreToUse >= 50) return "Getting closer";
    if (scoreToUse >= 25) return "Somewhat distant";
    return "Far off";
  };

  const proximityDescription = getProximityDescription();

  if (proximityScore !== undefined) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mb-2 flex items-center">
              {/* Left container - animate from right to left */}
              <div className="flex-1 flex justify-end items-center">
                <div 
                  className={`h-[1px] ${lineColorClass} transition-all duration-700 ease-out origin-right`}
                  style={{ width: leftLineWidth }}
                />
              </div>
              
              {/* Center "Round X" label */}
              <div className={`text-xs font-medium rounded-full py-[4px] px-[8px] text-muted-foreground bg-background border ${borderColorClass} z-10`}>
                {`Round ${roundNumber}`}
              </div>
              
              {/* Right container - animate from left to right */}
              <div className="flex-1 flex justify-start items-center">
                <div 
                  className={`h-[1px] ${lineColorClass} transition-all duration-700 ease-out origin-left`}
                  style={{ width: rightLineWidth }}
                />
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{proximityScore}% match</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="mb-2 flex items-center">
      {/* Left container */}
      <div className="flex-1"></div>
      
      {/* Center "Round X" label */}
      <div className={`text-xs font-medium rounded-full py-[4px] px-[8px] text-muted-foreground bg-background border ${borderColorClass} z-10`}>
        {`Round ${roundNumber}`}
      </div>
      
      {/* Right container */}
      <div className="flex-1"></div>
    </div>
  );
};
